import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Users, Globe, Lock, Shield, Camera, Image as ImageIcon, ChevronLeft, ArrowRight, CheckCircle2, Plus, X, Key, Zap, Loader2 } from 'lucide-react';
import { cn, getCommunityPath } from '../utils';
import { useActivities } from '../context/ActivitiesContext';
import { interests } from '../utils/interests';
import Breadcrumbs from '../components/Breadcrumbs';
import { handleImageUpload } from '../utils/imageUtils';

const CommunityCreation = () => {
  const navigate = useNavigate();
  const { addCommunity } = useActivities();
  const [step, setStep] = useState(1);
  const [privacy, setPrivacy] = useState('public');
  const [autoApprove, setAutoApprove] = useState(true);
  const [joinPassword, setJoinPassword] = useState('');
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [tagline, setTagline] = useState('');
  const [description, setDescription] = useState('');
  const [rules, setRules] = useState(['Be respectful', 'No spam', 'Stay on topic']);
  const [newRule, setNewRule] = useState('');
  const [coverImage, setCoverImage] = useState(null);
  const [communityIcon, setCommunityIcon] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const coverInputRef = React.useRef(null);
  const iconInputRef = React.useRef(null);

  const NAME_LIMIT = 50;
  const TAGLINE_LIMIT = 100;
  const DESCRIPTION_LIMIT = 2000;

  const handleFileChange = async (e, setter, maxSize = 2) => {
    const file = e.target.files[0];
    if (file) {
      try {
        setError('');
        const resizedImage = await handleImageUpload(file, { maxSizeMB: maxSize });
        setter(resizedImage);
      } catch (err) {
        setError(err.message);
      }
    }
  };

  const addRule = () => {
    if (newRule.trim()) {
      setRules([...rules, newRule.trim()]);
      setNewRule('');
    }
  };

  const removeRule = (index) => {
    setRules(rules.filter((_, i) => i !== index));
  };

  const handleCreate = async () => {
    if (!name.trim()) {
      setError('Please enter a community name');
      setStep(1);
      return;
    }
    if (name.length > NAME_LIMIT) {
      setError(`Community name is too long (max ${NAME_LIMIT} chars)`);
      setStep(1);
      return;
    }
    if (tagline.length > TAGLINE_LIMIT) {
      setError(`Tagline is too long (max ${TAGLINE_LIMIT} chars)`);
      setStep(1);
      return;
    }
    if (description.length > DESCRIPTION_LIMIT) {
      setError(`Description is too long (max ${DESCRIPTION_LIMIT} chars)`);
      setStep(1);
      return;
    }
    if (name.trim().length < 3) {
      setError('Community name must be at least 3 characters long');
      setStep(1);
      return;
    }
    if (description.trim().length < 20) {
      setError('About description must be at least 20 characters long');
      setStep(1);
      return;
    }
    if (privacy === 'private' && !joinPassword.trim()) {
      setError('Password is required for private communities');
      setStep(2);
      return;
    }

    try {
      const newCommunity = await addCommunity({
        name,
        tagline,
        description,
        privacy,
        settings: {
          auto_approve: privacy === 'private' ? autoApprove : true,
          join_password: privacy === 'private' ? joinPassword : null,
          category: category
        },
        rules,
        coverImage,
        icon: communityIcon,
        members: '1'
      });
      navigate(getCommunityPath(newCommunity));
    } catch (err) {
      setError(err.message || 'Failed to create community. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    if (step === 1) {
      if (!name.trim()) {
        setError('Please enter a community name');
        return;
      }
      if (name.length > NAME_LIMIT) {
        setError(`Community name is too long (max ${NAME_LIMIT} chars)`);
        return;
      }
      if (name.trim().length < 3) {
        setError('Community name must be at least 3 characters long');
        return;
      }
      if (!category) {
        setError('Please select a category');
        return;
      }
    }
    if (step === 2) {
      if (privacy === 'private' && !joinPassword.trim()) {
        setError('Password is required for private communities');
        return;
      }
    }
    setError('');
    setStep(step + 1);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 md:py-8">
      <div className="flex items-center gap-4 mb-6">
        <button 
          onClick={() => step > 1 ? setStep(step - 1) : navigate(-1)}
          className="p-3 hover:bg-gray-100 rounded-2xl transition-all text-gray-400 hover:text-gray-900"
        >
          <ChevronLeft size={24} />
        </button>
        <div>
          <div className="mb-1">
            <Breadcrumbs 
              items={[
                { label: 'Communities', path: '/communities' },
                { label: 'Create' }
              ]} 
            />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Create a Community</h1>
          <p className="text-gray-500 font-medium text-sm">Step {step} of 3: {step === 1 ? 'Identity' : step === 2 ? 'Access & Rules' : 'Review'}</p>
        </div>
      </div>

      {step === 1 ? (
        <motion.div 
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="space-y-6"
        >
          {error && (
            <div className="p-4 bg-red-50 border border-red-100 text-red-600 rounded-2xl text-sm font-bold flex items-center gap-2">
              <Shield size={16} />
              {error}
            </div>
          )}
          <div className="bg-white rounded-3xl border border-gray-100 overflow-hidden shadow-xl shadow-gray-100/50">
            <input 
              type="file" 
              ref={coverInputRef} 
              onChange={(e) => handleFileChange(e, setCoverImage, 5)} 
              accept="image/*" 
              className="hidden" 
            />
            <input 
              type="file" 
              ref={iconInputRef} 
              onChange={(e) => handleFileChange(e, setCommunityIcon, 1)} 
              accept="image/*" 
              className="hidden" 
            />
            <div className="h-40 bg-brand-indigo relative overflow-hidden">
               {coverImage && <img src={coverImage} alt="Cover" className="w-full h-full object-cover" />}
               <button 
                onClick={() => coverInputRef.current?.click()}
                className="absolute bottom-4 right-4 p-3 bg-white/20 hover:bg-white/30 text-white rounded-xl backdrop-blur-md transition-all"
               >
                  <Camera size={20} />
               </button>
            </div>
            <div className="px-6 pb-6 -mt-10">
              <div className="flex items-end gap-4 mb-6">
                <div className="w-24 h-24 rounded-3xl bg-white p-1.5 shadow-xl shadow-gray-200/50">
                   <div 
                    onClick={() => iconInputRef.current?.click()}
                    className="w-full h-full rounded-2xl bg-gray-50 flex items-center justify-center text-gray-300 relative group cursor-pointer hover:bg-gray-100 transition-all overflow-hidden"
                   >
                      {communityIcon ? (
                        <img src={communityIcon} alt="Icon" className="w-full h-full object-cover" />
                      ) : (
                        <ImageIcon size={32} />
                      )}
                      <div className="absolute inset-0 flex items-center justify-center bg-black/40 text-white opacity-0 group-hover:opacity-100 rounded-2xl transition-opacity">
                         <Camera size={20} />
                      </div>
                   </div>
                </div>
                <div className="flex-1 pb-1">
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-[0.15em]">Community Name</label>
                    <span className={cn("text-[10px] font-bold", name.length > NAME_LIMIT ? "text-red-500" : "text-gray-400")}>
                      {name.length}/{NAME_LIMIT}
                    </span>
                  </div>
                  <input 
                    type="text" 
                    value={name}
                    onChange={(e) => setName(e.target.value.slice(0, NAME_LIMIT + 10))}
                    placeholder="e.g. AI Researchers Collective"
                    className={cn(
                      "w-full px-4 py-2 border-b-2 bg-transparent font-bold text-2xl transition-all placeholder:text-gray-200 outline-none",
                      name.length > NAME_LIMIT ? "border-red-500 text-red-600" : "border-gray-100 focus:border-brand-indigo"
                    )}
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase tracking-[0.15em] mb-2">Category / Primary Interest</label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {interests.map((i) => (
                      <button
                        key={i.id}
                        type="button"
                        onClick={() => setCategory(i.id)}
                        className={cn(
                          "flex items-center gap-2 px-3 py-2 rounded-xl border-2 transition-all active:scale-95 text-left",
                          category === i.id 
                            ? "border-brand-indigo bg-brand-indigo/5 text-brand-indigo" 
                            : "border-gray-50 bg-white text-gray-400 hover:border-gray-100"
                        )}
                      >
                        <span className="shrink-0">{i.icon}</span>
                        <span className="text-[10px] font-bold uppercase tracking-[0.15em] truncate">{i.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-[0.15em]">One-line Tagline</label>
                    <span className={cn("text-[10px] font-bold", tagline.length > TAGLINE_LIMIT ? "text-red-500" : "text-gray-400")}>
                      {tagline.length}/{TAGLINE_LIMIT}
                    </span>
                  </div>
                  <input 
                    type="text" 
                    value={tagline}
                    onChange={(e) => setTagline(e.target.value.slice(0, TAGLINE_LIMIT + 20))}
                    placeholder="Briefly describe the community's purpose"
                    className={cn(
                      "w-full px-6 py-3 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-brand-indigo/20 font-medium transition-all",
                      tagline.length > TAGLINE_LIMIT && "ring-red-500/20"
                    )}
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-[0.15em]">About / Description</label>
                    <span className={cn("text-[10px] font-bold", description.length > DESCRIPTION_LIMIT ? "text-red-500" : "text-gray-400")}>
                      {description.length}/{DESCRIPTION_LIMIT}
                    </span>
                  </div>
                  <textarea 
                    rows="4"
                    value={description}
                    onChange={(e) => setDescription(e.target.value.slice(0, DESCRIPTION_LIMIT + 100))}
                    placeholder="Tell potential members what they can expect..."
                    className={cn(
                      "w-full px-6 py-3 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-brand-indigo/20 font-medium transition-all resize-none",
                      description.length > DESCRIPTION_LIMIT && "ring-red-500/20"
                    )}
                  ></textarea>
                </div>
              </div>
            </div>
          </div>

          <button 
            onClick={nextStep}
            className="w-full bg-brand-indigo text-white font-bold py-4 rounded-2xl shadow-xl shadow-brand-indigo/20 flex items-center justify-center gap-2 hover:bg-brand-indigo/90 transition-all active:scale-[0.98] text-lg"
          >
            Access & Rules
            <ArrowRight size={20} />
          </button>
        </motion.div>
      ) : step === 2 ? (
        <motion.div 
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="space-y-6"
        >
          {error && (
            <div className="p-4 bg-red-50 border border-red-100 text-red-600 rounded-2xl text-sm font-bold flex items-center gap-2">
              <Shield size={16} />
              {error}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             <div className="space-y-4">
                <section>
                   <h2 className="text-xs font-bold text-gray-400 uppercase tracking-[0.15em] mb-3">Who can join?</h2>
                   <div className="space-y-2">
                      <button 
                        onClick={() => setPrivacy('public')}
                        className={cn(
                          "w-full flex items-center gap-4 p-4 rounded-3xl border-2 transition-all active:scale-[0.99] text-left",
                          privacy === 'public' ? "border-brand-indigo bg-brand-indigo/5" : "border-gray-50 bg-white"
                        )}
                      >
                        <Globe size={20} className={privacy === 'public' ? "text-brand-indigo" : "text-gray-400"} />
                        <div>
                          <p className="font-bold text-gray-900 text-sm">Public</p>
                          <p className="text-xs text-gray-500 font-medium">Visible to all, anyone can join</p>
                        </div>
                      </button>
                      <button 
                        onClick={() => setPrivacy('private')}
                        className={cn(
                          "w-full flex items-center gap-4 p-4 rounded-3xl border-2 transition-all active:scale-[0.99] text-left",
                          privacy === 'private' ? "border-brand-indigo bg-brand-indigo/5" : "border-gray-50 bg-white"
                        )}
                      >
                        <Lock size={20} className={privacy === 'private' ? "text-brand-indigo" : "text-gray-400"} />
                        <div>
                          <p className="font-bold text-gray-900 text-sm">Private</p>
                          <p className="text-xs text-gray-500 font-medium">Members must be approved</p>
                        </div>
                      </button>
                   </div>

                   {privacy === 'private' && (
                     <motion.div
                       initial={{ opacity: 0, height: 0 }}
                       animate={{ opacity: 1, height: 'auto' }}
                       className="mt-4 p-5 bg-gray-50 rounded-3xl space-y-4"
                     >
                       <h3 className="text-xs font-bold text-gray-400 uppercase tracking-[0.15em]">Private Access Policy</h3>
                       
                       <button 
                         onClick={() => setAutoApprove(!autoApprove)}
                         className="w-full flex items-center justify-between group"
                       >
                         <div className="text-left">
                           <p className="font-bold text-gray-900 text-sm">Auto-Approve Requests</p>
                           <p className="text-xs text-gray-500 font-medium">Instantly approve new members</p>
                         </div>
                         <div className={cn("w-10 h-6 rounded-full relative transition-colors", autoApprove ? "bg-brand-indigo" : "bg-gray-300")}>
                           <div className={cn("absolute top-1 w-4 h-4 bg-white rounded-full transition-all", autoApprove ? "right-1" : "left-1")}></div>
                         </div>
                       </button>

                       <div className="space-y-2">
                         <div className="flex items-center gap-2 text-gray-400">
                           <Key size={14} />
                           <label className="text-xs font-bold uppercase tracking-[0.15em]">Join Password (Optional)</label>
                         </div>
                         <input 
                           type="text"
                           value={joinPassword}
                           onChange={(e) => setJoinPassword(e.target.value)}
                           placeholder="Require password to join"
                           className="w-full px-4 py-2.5 bg-white border-none rounded-xl focus:ring-2 focus:ring-brand-indigo/10 font-bold text-xs transition-all"
                         />
                       </div>
                     </motion.div>
                   )}
                </section>
             </div>
             
             <div className="space-y-4">
                <section>
                   <h2 className="text-xs font-bold text-gray-400 uppercase tracking-[0.15em] mb-3">Core Rules</h2>
                   <div className="bg-white rounded-3xl border border-gray-100 p-5 space-y-3">
                      {rules.map((rule, i) => (
                        <div key={i} className="flex items-center justify-between p-2.5 bg-gray-50 rounded-xl group">
                           <div className="flex items-center gap-2">
                             <CheckCircle2 size={14} className="text-brand-indigo" />
                             <span className="font-bold text-xs text-gray-700">{rule}</span>
                           </div>
                           <button onClick={() => removeRule(i)} className="text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all">
                             <X size={14} />
                           </button>
                        </div>
                      ))}
                      
                      <div className="flex gap-2">
                        <input 
                          type="text"
                          value={newRule}
                          onChange={(e) => setNewRule(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && addRule()}
                          placeholder="Type a new rule..."
                          className="flex-1 bg-gray-50 border-none rounded-xl px-4 py-2 text-xs font-bold focus:ring-2 focus:ring-brand-indigo/10 outline-none"
                        />
                        <button 
                          onClick={addRule}
                          className="p-2.5 bg-brand-indigo text-white rounded-xl hover:bg-brand-indigo/90 transition-all shadow-md shadow-brand-indigo/10"
                        >
                          <Plus size={16} />
                        </button>
                      </div>
                   </div>
                </section>
             </div>
          </div>

          <div className="bg-brand-indigo/5 border border-brand-indigo/10 rounded-3xl p-5 flex gap-4">
             <div className="p-3 bg-white rounded-2xl text-brand-indigo shadow-sm shrink-0">
                <Shield size={20} />
             </div>
             <div>
                <h4 className="font-bold text-brand-indigo text-sm">Community Moderation</h4>
                <p className="text-xs text-brand-indigo/70 font-medium leading-relaxed mt-0.5">
                   As the founder, you'll have access to member management, automated moderation tools, and community analytics.
                </p>
             </div>
          </div>

          <button 
            onClick={nextStep}
            className="w-full bg-brand-indigo text-white font-bold py-4 rounded-2xl shadow-xl shadow-brand-indigo/20 flex items-center justify-center gap-2 hover:bg-brand-indigo/90 transition-all active:scale-[0.98] text-lg"
          >
            Review Community
            <ArrowRight size={20} />
          </button>
        </motion.div>
      ) : (
        <motion.div 
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="space-y-6"
        >
          {error && (
            <div className="p-4 bg-red-50 border border-red-100 text-red-600 rounded-2xl text-sm font-bold flex items-center gap-2">
              <Shield size={16} />
              {error}
            </div>
          )}
          <div className="bg-white rounded-3xl border-2 border-brand-indigo p-6 text-center space-y-3">
             <div className="w-16 h-16 bg-brand-indigo/10 rounded-full flex items-center justify-center text-brand-indigo mx-auto mb-2">
                <Users size={32} />
             </div>
             <h2 className="text-2xl font-bold text-gray-900">Ready to launch?</h2>
             <p className="text-gray-500 font-medium max-w-sm mx-auto text-sm">
                Your community will be live immediately. You can customize the sub-items and permissions in the settings later.
             </p>
          </div>

          <button 
            onClick={handleCreate}
            disabled={loading}
            className={cn(
              "w-full bg-brand-indigo text-white font-bold py-4 rounded-2xl shadow-xl shadow-brand-indigo/20 flex items-center justify-center gap-2 hover:bg-brand-indigo/90 transition-all active:scale-[0.98] text-lg",
              loading && "opacity-70 cursor-not-allowed active:scale-100"
            )}
          >
            {loading ? (
              <div className="w-6 h-6 border-4 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              "Launch Community"
            )}
          </button>
        </motion.div>
      )}
    </div>
  );
};

export default CommunityCreation;
